import { Injectable } from '@angular/core';
import { ModelHelper } from '@dentsply-sirona/utilities';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Credentials } from '../interfaces/credentials.interface';
import { LinkedPartner } from '../models/linked-partner.model';
import { LinkedPartnerCredentialsSchema } from '../schemas/linked-partner-credentials.schema';
import { GraphqlService } from './graphql.service';

/**
 * Linked portal service
 * The DataService uses externalPartner
 * The CustomerFE uses linkedPartner
 * externalPartner === linkedPartner
 */
@Injectable({
	providedIn: 'root'
})
export class LinkedPartnerService extends GraphqlService {
	/**
	 * Get ExternalPartner via uid
	 *
	 * @param linkedPartnerUid
	 * @return
	 */
	public getLinkedPartner(linkedPartnerUid: string): Observable<LinkedPartner> {
		return this.query(LinkedPartnerCredentialsSchema.linkedPartner, {
			uid: linkedPartnerUid
		}).pipe(map((data: any) => LinkedPartner.map(data.externalpartner)));
	}

	/**
	 * Get linked/external partner names
	 * contractors = null
	 * contractors = empty array
	 * contractors = array with id(s)
	 */
	public getLinkedPartnerList(): Observable<LinkedPartner[]> {
		return this.query(LinkedPartnerCredentialsSchema.linkedPartnerList).pipe(
			map((data: any) => ModelHelper.mapArray(LinkedPartner, data.externalPartnerList))
		);
	}

	/**
	 * Check whether credentials for dynamical or non dynamical linked partner exists
	 */
	public hasCredentials(internalPartnerName: string): Observable<boolean> {
		return new Observable<boolean>((subscriber) => {
			this.sessionService.check().subscribe(() => {
				this.http
					.get(
						this.configService.config.linkedPartner.hasValidCredentialsUri +
							internalPartnerName +
							'/' +
							this.sessionService.currentUser.uid +
							'/',
						{
							headers: {
								Authorization: 'Bearer ' + this.sessionService.session.accessToken
							}
						}
					)
					.subscribe(
						(response: boolean) => {
							subscriber.next(response);
							subscriber.complete();
						},
						(error) => {
							subscriber.error(error);
						}
					);
			});
		});
	}

	/**
	 * Get credentials of fix linked/external  partners
	 */
	public getCredentials(internalPartnerName: string): Observable<Credentials> {
		return new Observable<Credentials>((subscriber) => {
			// Init observables
			const observables: Observable<any>[] = [];

			observables.push(
				this.query(LinkedPartnerCredentialsSchema.getCredentials, {
					externalPartnerName: internalPartnerName
				}).pipe(map((response: any) => response.getCredentials))
			);

			// Check if already done
			if (!observables.length) {
				subscriber.next({});
				subscriber.complete();
				return;
			}

			// Process get credentials requests
			forkJoin(observables).subscribe(
				(credentialsArray) => {
					// Standard
					let credentials: Credentials = credentialsArray[0];

					subscriber.next(credentials);
					subscriber.complete();
				},
				(error) => {
					subscriber.error(error);
				}
			);
		});
	}

	/**
	 * Set linked/external partner credentials for non dynamic partners
	 *
	 * HINT: At least one credential pair must be passed in credentialData
	 */
	public setCredentials(internalPartnerName: string, credentialData: Credentials): Observable<boolean> {
		return new Observable<boolean>((subscriber) => {
			// Init observables
			const observables: Observable<boolean>[] = [];

			// Check credential pairs
			if (observables.length === 0) {
				subscriber.error(new Error('No valid credential pair.'));
			}

			// Process credential pairs
			forkJoin(observables).subscribe(
				() => {
					subscriber.next(true);
					subscriber.complete();
				},
				(error) => {
					subscriber.error(error);
				}
			);
		});
	}

	/**
	 * Open linked/external partner login in current tab
	 */
	public linkedPartnerLogin(internalPartnerName: string) {
		this.windowRef.nativeWindow.location.href = this.configService.config.linkedPartner.loginUri + internalPartnerName;
	}

	/**
	 * Link linked/external partner
	 */
	public linkedPartnerLink(internalPartnerName: string, code: string, idToken: string): Observable<boolean> {
		return new Observable<boolean>((subscriber) => {
			this.sessionService.check().subscribe(() => {
				this.http
					.post(
						this.configService.config.linkedPartner.linkUri + internalPartnerName + '/',
						{ code, idToken },
						{
							headers: {
								Authorization: 'Bearer ' + this.sessionService.session.accessToken,
								'Session-Id': this.sessionService.session.uid
							}
						}
					)
					.subscribe(
						(response) => {
							subscriber.next(true);
							subscriber.complete();
						},
						(error) => {
							subscriber.error(error);
						}
					);
			});
		});
	}

	/**
	 * Unlink linked/external partner
	 */
	public linkedPartnerUnlink(internalPartnerName: string): Observable<boolean> {
		return new Observable<boolean>((subscriber) => {
			this.sessionService.check().subscribe(() => {
				this.http
					.get(this.configService.config.linkedPartner.unlinkUri + internalPartnerName + '/', {
						headers: {
							Authorization: 'Bearer ' + this.sessionService.session.accessToken,
							'Session-Id': this.sessionService.session.uid
						}
					})
					.subscribe(
						(response) => {
							subscriber.next(true);
							subscriber.complete();
						},
						(error) => {
							subscriber.error(error);
						}
					);
			});
		});
	}

	/**
	 * Logout on partner platform
	 */
	public logoutOnPartnerPlatform(internalPartnerName: string) {
		this.windowRef.nativeWindow.location.href =
			this.configService.config.linkedPartner.endSession + internalPartnerName + '/' + this.sessionService.currentUser.uid + '/';
	}
}
